import { useContext, useMemo } from 'react';
import { AuthContext } from '../contexts/AuthContext';

/**
 * Hook for checking user roles and permissions
 * @returns {Object} Permission checking functions
 *
 * @example
 * const { hasRole, hasPermission, hasAnyRole, hasAnyPermission } = usePermissions();
 *
 * if (hasRole('SADM')) {
 *   // Show admin content
 * }
 *
 * if (hasPermission('users.create')) {
 *   // Show create user button
 * }
 */
const usePermissions = () => {
  const { user } = useContext(AuthContext);

  const userRoles = useMemo(() => {
    if (!user) return [];

    // Handle both roles array (TNL style) and single role string (LH style)
    if (user.roles && Array.isArray(user.roles)) {
      return user.roles.map(r => typeof r === 'string' ? r : r.name);
    } else if (user.role) {
      return [user.role];
    }
    return [];
  }, [user]);

  const userPermissions = useMemo(() => {
    if (!user || !user.permissions) return [];
    return user.permissions.map(p => typeof p === 'string' ? p : p.name);
  }, [user]);

  /**
   * Check if user has a specific role
   * @param {string} role - Role name to check
   * @returns {boolean}
   */
  const hasRole = (role) => {
    return userRoles.includes(role);
  };

  /**
   * Check if user has any of the specified roles
   * @param {string[]} roles - Array of role names
   * @returns {boolean}
   */
  const hasAnyRole = (roles) => {
    return roles.some(role => userRoles.includes(role));
  };

  /**
   * Check if user has all of the specified roles
   * @param {string[]} roles - Array of role names
   * @returns {boolean}
   */
  const hasAllRoles = (roles) => {
    return roles.every(role => userRoles.includes(role));
  };

  /**
   * Check if user has a specific permission
   * @param {string} permission - Permission name to check
   * @returns {boolean}
   */
  const hasPermission = (permission) => {
    return userPermissions.includes(permission);
  };

  /**
   * Check if user has any of the specified permissions
   * @param {string[]} permissions - Array of permission names
   * @returns {boolean}
   */
  const hasAnyPermission = (permissions) => {
    return permissions.some(permission => userPermissions.includes(permission));
  };

  /**
   * Check if user has all of the specified permissions
   * @param {string[]} permissions - Array of permission names
   * @returns {boolean}
   */
  const hasAllPermissions = (permissions) => {
    return permissions.every(permission => userPermissions.includes(permission));
  };

  /**
   * Check if user is a super admin (SADM role)
   * @returns {boolean}
   */
  const isSuperAdmin = () => {
    return hasRole('SADM');
  };

  return {
    hasRole,
    hasAnyRole,
    hasAllRoles,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isSuperAdmin,
    userRoles,
    userPermissions
  };
};

export default usePermissions;
